/**
 * Tank Registry — central registration and lookup for all tank definitions.
 */

import type { TankDef, ShotTierDef } from './types';

// ─── Tank Registry ──────────────────────────────────────────────────────────

const TANK_REGISTRY: Record<string, TankDef> = {};

export function registerTank(tank: TankDef): TankDef {
  TANK_REGISTRY[tank.id] = tank;
  return tank;
}

export function lookupTank(id: string): TankDef {
  return TANK_REGISTRY[id] ?? TANK_REGISTRY['mortar'];
}

export function getTankShot(tankId: string, tier: number, terraformerMode?: 'sapper' | 'mason'): ShotTierDef {
  const shots = getEffectiveShots(tankId, terraformerMode);
  // Guard: if tier exceeds shot count, fall back to tier 1
  if (tier - 1 >= shots.length) return shots[0];
  return shots[tier - 1];
}

/** Returns mode-aware shots for Terraformer, or normal shots for other tanks. */
export function getEffectiveShots(tankId: string, terraformerMode?: 'sapper' | 'mason'): ShotTierDef[] {
  if (tankId === 'terraformer' && terraformerMode === 'mason') {
    return TERRAFORMER_MASON_SHOTS;
  }
  const tank = lookupTank(tankId);
  return tank.shots;
}

// Terraformer mode-specific shot array (populated by terraformer.ts via setter)
let TERRAFORMER_MASON_SHOTS: ShotTierDef[];

export function setTerraformerMasonShots(shots: ShotTierDef[]): void {
  TERRAFORMER_MASON_SHOTS = shots;
}

export function getDefaultTank(): TankDef {
  return TANK_REGISTRY['mortar'];
}

export function getAllTankIds(): string[] {
  return Object.keys(TANK_REGISTRY);
}

export function getSelectableTankIds(): string[] {
  return Object.keys(TANK_REGISTRY).filter(id => !TANK_REGISTRY[id].disabled);
}

export function isTankDisabled(id: string): boolean {
  return TANK_REGISTRY[id]?.disabled === true;
}

// ─── Bonus Tank Toggle (local UI state, not synced) ─────────────────────────
let showBonusTanks = false;

export function getShowBonusTanks(): boolean {
  return showBonusTanks;
}

export function setShowBonusTanks(show: boolean): void {
  showBonusTanks = show;
}

/** Returns tank IDs visible for selection. In online mode, respects bonus toggle and online disable. */
export function getVisibleTankIds(isOnline: boolean): string[] {
  const all = getAllTankIds();
  if (!isOnline) return all;
  const online = all.filter(id => !TANK_REGISTRY[id].onlineDisabled);
  if (showBonusTanks) return online;
  return online.filter(id => !TANK_REGISTRY[id].bonus);
}

export function isBonusTank(id: string): boolean {
  return TANK_REGISTRY[id]?.bonus === true;
}

export function getGridDimensions(tankCount: number): { cols: number; rows: number } {
  if (tankCount <= 4) return { cols: 4, rows: 1 };
  if (tankCount <= 10) return { cols: 5, rows: 2 };
  return { cols: 6, rows: 2 };
}

